import { useMemo } from 'react';
import { Gender, GrowthDataSet, IntervalType } from '../types/data-set.types';
import { AgeRangeOption } from '../options/age-range-options';
import { getAdjustedAge } from '../data/mock-patient';

// -----------------------------------------------------------------------------
export interface GrowthChartDataProps {
  selectedDataset: GrowthDataSet | undefined;
  currentOption: AgeRangeOption;
  patientData: any;
  showPatientData: boolean;
  showAdjustedData: boolean;
  isWeightForLength: boolean;
}

// -----------------------------------------------------------------------------
export const useGrowthChartData = ({
  selectedDataset,
  currentOption,
  patientData,
  showPatientData,
  showAdjustedData,
  isWeightForLength,
}: GrowthChartDataProps) => {
  // ---------------------------------------------------------------------------
  // Unified data processing - separate percentiles and patient data
  const {
    percentileData,
    patientData: patientPoints,
    adjustedPatientData: adjustedPatientPoints,
  } = useMemo(() => {
    if (!selectedDataset) {
      return { percentileData: [], patientData: [], adjustedPatientData: [] };
    }

    // Determine whether to use length or age as the x-axis
    const xAxisKey = isWeightForLength ? 'length' : 'age';

    // Helper function to check if a point is within range
    const isPointInRange = (value: number): boolean => {
      if (isWeightForLength && currentOption.lengthFilter) {
        return currentOption.lengthFilter(value);
      } else if (
        !isWeightForLength &&
        currentOption.ageFilter &&
        selectedDataset.intervalType
      ) {
        return currentOption.ageFilter(value, selectedDataset.intervalType);
      }
      return true;
    };

    // Process the percentile data
    const percentiles = selectedDataset.dataPoints
      .filter((point) => {
        const xValue = isWeightForLength ? point.length : point.age;
        if (xValue === undefined) {
          return false;
        }
        return isPointInRange(xValue);
      })
      .map((point) => {
        const xValue = isWeightForLength ? point.length : point.age;
        return {
          [xAxisKey]: xValue,
          p1: point.percentiles.p1,
          p3: point.percentiles.p3,
          p5: point.percentiles.p5,
          p15: point.percentiles.p15,
          p25: point.percentiles.p25,
          p50: point.percentiles.p50,
          p75: point.percentiles.p75,
          p85: point.percentiles.p85,
          p95: point.percentiles.p95,
          p97: point.percentiles.p97,
          p99: point.percentiles.p99,
        };
      })
      .sort((a, b) => {
        const aValue = a[xAxisKey];
        const bValue = b[xAxisKey];
        if (aValue === undefined || bValue === undefined) {
          return 0;
        }

        return aValue - bValue;
      });

    // If we're not showing patient data or don't have patient data, return just the percentiles
    if (!showPatientData || !patientData?.measurements) {
      return {
        percentileData: percentiles,
        patientData: [],
        adjustedPatientData: [],
      };
    }

    // Process patient data separately
    const patientPoints = patientData.measurements
      .filter((measurement: any) => {
        const xValue = isWeightForLength ? measurement.length : measurement.age;
        if (xValue === undefined) {
          return false;
        }
        return isPointInRange(xValue);
      })
      .map((measurement: any) => {
        const xValue = isWeightForLength ? measurement.length : measurement.age;
        return {
          [xAxisKey]: xValue,
          patientValue: measurement.value,
          ageObject: measurement.ageObj,
        };
      })
      .sort((a: any, b: any) => {
        const aValue = a[xAxisKey];
        const bValue = b[xAxisKey];
        return aValue - bValue;
      });

    // Process adjusted patient data for gestational age
    let adjustedPatientPoints = [];

    if (
      showAdjustedData &&
      !isWeightForLength &&
      patientData.gestationalAgeAtBirth &&
      selectedDataset.intervalType
    ) {
      // First, transform data as before
      const rawAdjustedPoints = patientData.measurements
        .filter((measurement: any) => {
          const xValue = measurement.age;
          if (xValue === undefined) {
            return false;
          }
          return isPointInRange(xValue);
        })
        .map((measurement: any) => {
          // Calculate adjusted age
          const adjustedAge = getAdjustedAge(
            measurement.age,
            patientData.gestationalAgeAtBirth,
            selectedDataset.intervalType as IntervalType
          );

          return {
            [xAxisKey]: adjustedAge,
            adjustedValue: measurement.value,
          };
        })
        // Filter again to ensure adjusted ages are still within range
        .filter((point: any) => isPointInRange(point[xAxisKey]));

      // Group by age and keep only the highest value for each age
      const ageValueMap = new Map();

      rawAdjustedPoints.forEach((point: any) => {
        const age = point[xAxisKey];
        const currentValue = point.adjustedValue;

        if (
          !ageValueMap.has(age) ||
          currentValue > ageValueMap.get(age).adjustedValue
        ) {
          ageValueMap.set(age, point);
        }
      });

      // Convert map back to array and sort by age
      adjustedPatientPoints = Array.from(ageValueMap.values()).sort(
        (a: any, b: any) => {
          const aValue = a[xAxisKey];
          const bValue = b[xAxisKey];
          return aValue - bValue;
        }
      );
    }

    return {
      percentileData: percentiles,
      patientData: patientPoints,
      adjustedPatientData: adjustedPatientPoints,
    };
  }, [
    selectedDataset,
    currentOption,
    patientData,
    showPatientData,
    showAdjustedData,
    isWeightForLength,
  ]);

  // ---------------------------------------------------------------------------
  // Generate chart labels and formatting helpers
  const getChartLabels = (
    selectedDataset: GrowthDataSet | undefined,
    measurementTypeText: string,
    isWeightForLength: boolean,
    selectedGender: Gender,
    currentOption: AgeRangeOption
  ) => {
    const isWeekly = selectedDataset?.intervalType === 'week';
    const genderText = selectedGender === 'male' ? 'Boys' : 'Girls';
    const ageRangeText = currentOption.displayName.split(' (')[0];

    // Determine x-axis label based on chart type
    const xAxisLabel = isWeightForLength
      ? 'Length (cm)'
      : `Age (${selectedDataset?.intervalType || 'months'}s)`;

    const yAxisLabel = `${
      isWeightForLength ? 'Weight' : measurementTypeText
    } (${selectedDataset?.displayUnit || ''})`;

    const chartTitle = isWeightForLength
      ? `Weight-for-length (${genderText}, ${ageRangeText})`
      : `${measurementTypeText}-for-age (${genderText}, ${ageRangeText})`;

    const formatXAxisTick = (value: number) => {
      if (isWeightForLength) {
        return value.toString();
      }

      if (currentOption.id.includes('0-60') && value >= 12) {
        return `${Math.floor(value / 12)}y`;
      }
      return value.toString();
    };

    const formatTooltipLabel = (value: number) => {
      if (isWeightForLength) {
        return `Length: ${value} cm`;
      }

      if (isWeekly) {
        return `Week ${value}`;
      }

      if (value >= 12 && currentOption.id.includes('0-60')) {
        const years = Math.floor(value / 12);
        const months = value % 12;
        return months > 0 ? `${years}y ${months}m` : `${years}y`;
      }

      return `Month ${value}`;
    };

    return {
      xAxisLabel,
      yAxisLabel,
      chartTitle,
      formatXAxisTick,
      formatTooltipLabel,
    };
  };

  // ---------------------------------------------------------------------------
  return {
    percentileData,
    patientPoints,
    adjustedPatientPoints,
    getChartLabels,
  };
};
